import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TextFieldModule } from '@angular/cdk/text-field';
import { ApiService } from '../../../service/api.service';
import { ChatbotService } from '../../../service/chatbot.service';
import { FabService } from '../../../service/fab.service';
import { PowService, PowSolution } from '../../../service/pow.service';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  toolCalls?: any[];
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  read_knowledge_base: 'Read Knowledge Base',
  get_user_profile: 'Looked Up Profile'
};

function toolCallsFromUsage(toolUsage: Record<string, number> | undefined): any[] {
  return Object.entries(toolUsage || {}).map(([tool, count]) => {
    const baseName = TOOL_DISPLAY_NAMES[tool] || tool;
    return {
      tool,
      display_name: count > 1 ? `${baseName} (×${count})` : baseName,
      parameters: {}
    };
  });
}

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTooltipModule,

    TextFieldModule
  ],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.less'
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnChanges {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;
  @Input() isOpen = false;
  @Input() mode: 'side' | 'over' = 'side';
  @Input() position: 'start' | 'end' = 'end';
  @Input() width = '400px';
  @Output() openedChange = new EventEmitter<boolean>();
  
  messages: ChatMessage[] = [];
  currentMessage = '';
  isLoading = false;
  suggestions: string[] = [];
  isFullscreen = false;
  private chatFabId: number | null = null;

  // Proof of work: every message needs its own solution. One is solved ahead of
  // time (once the chat is first opened) so sending is instant, and a new one
  // starts as soon as it is used.
  powPending = false;
  private powPromise: Promise<PowSolution> | null = null;
  
  // Custom sidebar positioning
  get sidebarTop(): string {
    return this.isFullscreen ? '0px' : '64px'; // 64px for navbar height
  }

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private chatbotService: ChatbotService,
    private fabService: FabService,
    private powService: PowService
  ) {}

  ngOnInit() {
    // Add welcome message
    const welcomeMessage = {
      id: this.generateId(),
      type: 'assistant' as const,
      content: 'Hello! I\'m CultBot, your helpful assistant. I can help you with your patreon perks and other questions realted to Jar or her worlds. What would you like to know?',
      timestamp: new Date(),
      suggestions: [
        'I am having issues with my patreon perks',
        'How can I submit a script for Audience Anarchy?',
        'What else can you do?'
      ]
    };
    
    this.addMessage(welcomeMessage);
    this.suggestions = welcomeMessage.suggestions;
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      if (!this.powPromise) {
        this.startPow();
      }
      // Focus the input when chatbot opens
      setTimeout(() => {
        this.focusInput();
      }, 100);
      
      // Don't remove FAB when reopening from minimized state
      // FAB should only be removed when fully closed
    }
  }

  onOpenedChange(opened: boolean) {
    this.isOpen = opened;
    this.openedChange.emit(opened);
  }

  closeSidebar() {
    this.isOpen = false;
    this.openedChange.emit(false);
    
    // Remove the FAB when chatbot is fully closed
    if (this.chatFabId !== null) {
      this.fabService.removeItem(this.chatFabId);
      this.chatFabId = null;
    }
  }

  minimizeSidebar() {
    this.chatbotService.minimizeChatbot();
    
    // Add FAB to allow reopening the chat when minimized
    if (this.chatFabId === null) {
      this.chatFabId = this.fabService.addItem(
        "smart_toy", 
        "Open Chat", 
        false, 
        () => {
          this.chatbotService.restoreChatbot();
        },
        true // noAutoclear = true so it persists until manually removed
      );
    }
  }

  private startPow(): Promise<PowSolution> {
    this.powPending = true;
    const promise = this.powService.solve('ai');
    this.powPromise = promise;
    const settle = () => {
      if (this.powPromise === promise) this.powPending = false;
    };
    promise.then(settle, settle);
    return promise;
  }

  // Hands out the current solution (waiting for it if needed) and starts the next one
  private async takePow(): Promise<PowSolution> {
    let promise = this.powPromise ?? this.startPow();
    let pow: PowSolution | null = null;
    try {
      pow = await promise;
    } catch (e) {
      console.error('Failed to solve chat challenge:', e);
    }
    // Challenges expire server side; a pre-solved one may have gone stale
    if (!pow || parseInt(pow.c.split('.')[0], 10) < Date.now() / 1000 + 10) {
      pow = await this.startPow();
    }
    this.startPow();
    return pow;
  }

  sendMessage(message?: string) {
    const messageText = message || this.currentMessage.trim();
    
    if (!messageText || this.isLoading) {
      return;
    }

    // Typed messages wait for the button to become available; suggestion chips
    // are queued and sent once the proof of work completes
    if (!message && this.powPending) {
      return;
    }

    // Clear suggestions when user sends a new message
    this.suggestions = [];

    // Add user message
    const userMessage: ChatMessage = {
      id: this.generateId(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };
    
    this.addMessage(userMessage);
    this.currentMessage = '';
    this.isLoading = true;

    // Prepare unified chat history for the AI backend
    const history = this.messages
      .filter(m => m.type === 'user' || m.type === 'assistant')
      .slice(-10) // Last 10 messages for context
      .map(m => ({
        role: m.type,
        author: m.type === 'user' ? 'You' : 'CultBot',
        author_id: null,
        content: m.content,
        reply_to_author: null
      }));

    this.takePow().then(pow => this.postMessage(messageText, history, pow)).catch(error => this.handleSendError(error));
  }

  private postMessage(messageText: string, history: any[], pow: PowSolution) {
    this.apiService.post('ai/chat.php', {
      conversation_type: 'one_on_one',
      message: messageText,
      history,
      pow
    }).subscribe({
      next: (response) => {

        if (response.code == 403) {
          // Add assistant response
          const assistantMessage: ChatMessage = {
            id: this.generateId(),
            type: 'assistant',
            content: "Hello! You need to log in before you can talk to me",
            timestamp: new Date(),
            suggestions: [],
            toolCalls: []
          };

          this.addMessage(assistantMessage);
          return;
        }

        // Add assistant response
        const assistantMessage: ChatMessage = {
          id: this.generateId(),
          type: 'assistant',
          content: response.response,
          timestamp: new Date(),
          suggestions: response.suggestions || [],
          toolCalls: toolCallsFromUsage(response.tool_usage)
        };

        this.addMessage(assistantMessage);
        // Suggestions will be updated by addMessage method
      },
      error: (error) => this.handleSendError(error),
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private handleSendError(error: any) {
    console.error('Error sending message:', error);
    this.isLoading = false;
    this.addMessage({
      id: this.generateId(),
      type: 'assistant',
      content: 'Sorry, I encountered an error while processing your request. Please try again.',
      timestamp: new Date()
    });

    this.snackBar.open('Failed to send message', 'Close', {
      duration: 3000,
      panelClass: ['error-snackbar']
    });
  }

  useSuggestion(suggestion: string) {
    this.currentMessage = suggestion;
    this.sendMessage(suggestion);
  }

  private addMessage(message: ChatMessage) {
    this.messages.push(message);
    
    // Update suggestions if the message has them
    if (message.suggestions && message.suggestions.length > 0) {
      this.suggestions = message.suggestions;
    }
  }

  private scrollToBottom() {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      // Ignore scroll errors
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  formatMessageContent(content: string): string {
    // Convert line breaks to <br> tags for proper HTML display
    return content.replace(/\n/g, '<br>');
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearChat() {
    this.messages = [];
    this.suggestions = [];
    this.ngOnInit(); // Re-add welcome message with suggestions
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.mode = 'over';
      this.width = '100vw';
    } else {
      this.mode = 'side';
      this.width = '400px';
    }
  }

  private focusInput() {
    if (this.messageInput && this.messageInput.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }
}


